import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { setSetUnitsCompleted } from 'redux/slices/activeWorkout'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid'

export default function VolumeCompleted({ 
    version = '4.0',
    profileType,
    groupId,
    athleteId,
    phaseIndex, 
    exerciseIndex, 
    exercise,
    setIndex, 
    set, 
    isCompleted, 
    isSkipped,
    showLabel = true
}) {

    const dispatch = useDispatch()

    const disableCardInputs = isCompleted || isSkipped

    var setUnitsAssigned = 0
    if (exercise.setUnit === 'reps') setUnitsAssigned = set.reps
    if (exercise.setUnit === 'seconds') setUnitsAssigned = set.seconds
    if (version === '5.0'){ 
        setUnitsAssigned = set?.setUnitsAssigned
    }

    var setUnitsCompleted = 0
    if (profileType === 'group') setUnitsCompleted = set.completedSetData.setUnitsCompleted
    if (profileType === 'group-individual') setUnitsCompleted = set.groupIndividualCompletedSetData[athleteId].setUnitsCompleted
    if (profileType === 'individual') setUnitsCompleted = set.completedSetData.setUnitsCompleted

    const [isScaling, setIsScaling] = useState(false)
    const [isInputBoxCurrentlyFocused, setIsInputBoxCurrentlyFocused] = useState(false)
    const [inputBoxValue, setInputBoxValue] = useState(setUnitsCompleted)

    // Increment/decrement functions

    const IncrementSetUnitsCompleted = () => {
        if (setUnitsCompleted < setUnitsAssigned) {

            const payload = {
                profileType: profileType,
                groupId: groupId,
                athleteId: athleteId,
                phaseIndex: phaseIndex,
                exerciseIndex: exerciseIndex,
                setIndex: setIndex,
                setUnitsCompleted: setUnitsCompleted + 1
            }

            dispatch(setSetUnitsCompleted(payload))
        }

        TriggerScaleAnimation()
    }

    const DecrementSetUnitsCompleted = () => {
        if (setUnitsCompleted > 0) {

            const payload = {
                profileType: profileType,
                groupId: groupId,
                athleteId: athleteId,
                phaseIndex: phaseIndex,
                exerciseIndex: exerciseIndex,
                setIndex: setIndex,
                setUnitsCompleted: setUnitsCompleted - 1
            }

            dispatch(setSetUnitsCompleted(payload))
        }

        TriggerScaleAnimation()
    }

    const TriggerScaleAnimation = () => {
        setIsScaling(true)
        setTimeout(() => setIsScaling(false), 200)
    }

    // Input box functions

    const HandleInputBoxFocus = () => {

        if (disableCardInputs) return

        setIsInputBoxCurrentlyFocused(true)
        setInputBoxValue('')

    }

    const HandleSetUnitsCompletedChange = (value) => {

        if (disableCardInputs) return

        // Allow empty string
        if (value === '') {
            setInputBoxValue('')
            return
        }

        // Only allow digits, max 2 characters
        const numericValue = value.replace(/[^0-9]/g, '')

        if (numericValue.length > 2) return

        // Only update local state - no Redux dispatching
        setInputBoxValue(numericValue)
    }

    const HandleSetUnitsCompletedBlur = () => {

        if (disableCardInputs) return

        // If input is empty, just reset focus state (don't update Redux)
        if (inputBoxValue === '') {
            setIsInputBoxCurrentlyFocused(false)
            return
        }

        // Parse and validate the input value
        const currentValue = parseInt(inputBoxValue, 10) || 0
        const clampedValue = Math.max(0, Math.min(currentValue, setUnitsAssigned))

        const payload = {
            profileType: profileType,
            groupId: groupId,
            athleteId: athleteId,
            phaseIndex: phaseIndex,
            exerciseIndex: exerciseIndex,
            setIndex: setIndex,
            setUnitsCompleted: clampedValue
        }

        dispatch(setSetUnitsCompleted(payload))

        // Reset local state
        setInputBoxValue('')
        setIsInputBoxCurrentlyFocused(false)
    }

    if (isSkipped === true) {
        return null
    }

    return (
        <div className='space-y-2'>
            {showLabel && (
                <div className='h-4 text-xs text-center text-gray-600'>
                    {exercise.setUnit === 'reps' && <>Reps</>}
                    {exercise.setUnit === 'seconds' && <>Seconds</>}
                </div>
            )}

            <div className='flex items-center justify-center'>
                {disableCardInputs === false &&
                    <div 
                        className='flex-1 pr-1 py-4' // bg-red-100
                        onClick={() => DecrementSetUnitsCompleted()}
                    >
                        <ChevronLeftIcon className='ml-auto w-10 h-10 text-gray-600' />
                    </div>
                }

                <div 
                    className={`
                        relative 
                        ${isScaling ? 'animate-scale-bounce' : ''}
                    `}
                >
                    <input 
                        type="text" 
                        inputMode="numeric"
                        className='w-20 h-20 rounded-xl flex items-center justify-center text-4xl text-center text-gray-900 pb-3 border border-gray-400 bg-white'
                        value={isInputBoxCurrentlyFocused ? inputBoxValue : String(setUnitsCompleted)} 
                        disabled={disableCardInputs}
                        onFocus={HandleInputBoxFocus}
                        onChange={(event) => HandleSetUnitsCompletedChange(event.target.value)}
                        onBlur={HandleSetUnitsCompletedBlur}
                    />

                    <div className='absolute bottom-2 left-0 w-full text-center text-xs text-gray-800'>
                        {exercise.setUnit === 'reps' &&
                            <>
                                {exercise.lateral === 'unilateral' && <>per side</>}
                                {exercise.lateral === 'bilateral' && <>total</>}
                            </>
                        }
                        {exercise.setUnit === 'seconds' && 
                            <>
                                {exercise.lateral === 'unilateral' && <>sec / side</>}
                                {exercise.lateral === 'bilateral' && <>seconds</>}
                            </>
                        }
                    </div>
                </div>

                {disableCardInputs === false &&
                    <div 
                        className='flex-1 pl-1 py-4' // bg-red-100
                        onClick={() => IncrementSetUnitsCompleted()}
                    >
                        <ChevronRightIcon className='mr-auto w-10 h-10 text-gray-600' />
                    </div>
                }
            </div>
        </div>
    )
}
